import { useState, useEffect, useMemo } from "react";
import { usePathname } from "next/navigation";
import BoxBlock from "../ui/BoxBlock";
import { Cookie, Loader2 } from "lucide-react";
import Button from "../ui/Button";
import Link from "next/link";
import CookieSettingsPanel from "./CookieSettingsPanel";

function getCookie(name) {
  if (typeof document === "undefined") return null;

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  if (parts.length === 2) {
    return parts.pop().split(";").shift();
  }

  return null;
}

export default function CookieConsent() {
  const pathname = usePathname();

  const [visible, setVisible] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [detailedOpen, setDetailedOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // 🔒 Azokon az oldalakon NE jelenjen meg
  const excludedPaths = useMemo(
    () => ["/pages/adatkezelesi-tajekoztato", "/pages/aszf", "/debug"],
    []
  );

  const isExcluded = excludedPaths.includes(pathname);

  // ✅ Hookok mindig lefutnak, NINCS return előttük
  useEffect(() => {
    if (typeof window === "undefined") return;

    const adminFlag = localStorage.getItem("is_admin") === "true";
    setIsAdmin(adminFlag);

    // excluded vagy admin → se banner, se scroll lock
    if (adminFlag || isExcluded) {
      setVisible(false);
      document.body.style.overflow = "";
      return;
    }

    const accepted = sessionStorage.getItem("cookie_consent");

    if (accepted) {
      setVisible(false);
      document.body.style.overflow = "";
    } else {
      setVisible(true);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isExcluded]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isExcluded) return;

    const params = new URLSearchParams(window.location.search);

    const referrer =
      document.referrer &&
        !document.referrer.includes(window.location.hostname)
        ? document.referrer
        : null;

    const utm = {
      source: params.get("utm_source"),
      medium: params.get("utm_medium"),
      campaign: params.get("utm_campaign"),
      term: params.get("utm_term"),
      content: params.get("utm_content"),
      referrer,
    };

    const hasAny = Object.values(utm).some((v) => v);

    if (hasAny) {
      localStorage.setItem("utm_data", JSON.stringify(utm));
    }
  }, [isExcluded]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get("fbclid");

    if (!fbclid) return;

    const existingFbc = getCookie("_fbc");

    if (!existingFbc) {
      const fbc = `fb.1.${Date.now()}.${fbclid}`;

      document.cookie = `_fbc=${fbc}; path=/; max-age=7776000; SameSite=Lax; Secure`;

      localStorage.setItem("fbc", fbc);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handler = () => {
      const storeCookies = () => {
        const fbp = getCookie("_fbp");
        const fbc = getCookie("_fbc");

        if (fbp && !localStorage.getItem("fbp")) {
          localStorage.setItem("fbp", fbp);
        }

        if (fbc && !localStorage.getItem("fbc")) {
          localStorage.setItem("fbc", fbc);
        }
      };

      storeCookies();

      setTimeout(storeCookies, 800);
      setTimeout(storeCookies, 2000);

      setVisible(false);
      document.body.style.overflow = "";
    };

    window.addEventListener("cookieAccepted", handler);
    return () => window.removeEventListener("cookieAccepted", handler);
  }, []);

  const acceptCookies = async () => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      const consent = {
        necessary: true,
        analytics: true,
        marketing: true,
        functional: true,
        accepted_at: new Date().toISOString(),
      };

      sessionStorage.setItem("cookie_consent", JSON.stringify(consent));

      const utmRaw = localStorage.getItem("utm_data");
      const utm = utmRaw ? JSON.parse(utmRaw) : null;
      const fbp = localStorage.getItem("fbp");
      const fbc = localStorage.getItem("fbc");

      const user_agent = navigator.userAgent;

      const res = await fetch("/api/guest/init", {
        method: "POST",
        body: JSON.stringify({ utm, consent, fbp, fbc, user_agent }),
      });

      const data = await res.json();

      if (!data?.session_id) {
        console.error("No session_id returned from Strapi");
        return;
      }

      if (data.consent_id) {
        localStorage.setItem("consent_id", data.consent_id);
      }

      localStorage.removeItem("utm_data");

      const cookieRes = await fetch("/api/set-guest-jwt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: data.session_id }),
      });

      const cookieData = await cookieRes.json();

      if (!cookieData?.success) {
        console.error("Cookie set failed:", cookieData);
        return;
      }

      setVisible(false);
      document.body.style.overflow = "";

      window.dispatchEvent(new Event("cookieAccepted"));
    } catch (err) {
      console.error("Guest init failed:", err);
    } finally {
      setIsSaving(false);
    }
  };

  // ✅ return csak hookok után
  if (isExcluded) return null;
  if (!visible) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998]" />

      <div className="bottom-4 fixed z-[9999] w-auto mx-4 md:mx-0 md:right-4 md:max-w-sm">
        <BoxBlock
          title="Az oldalunk sütiket használ!"
          icon={<Cookie />}
          className="divide-y divide-gray-100 dark:divide-zinc-700"
          headerClass=""
          footer={
            <div className="flex gap-4">
              <Link
                href="/pages/adatkezelesi-tajekoztato"
                className="text-gray-700 dark:text-gray-300"
              >
                Adatkezelési tájékoztató
              </Link>
              <Link href="/pages/aszf" className="text-gray-700 dark:text-gray-300">
                ÁSZF
              </Link>
            </div>
          }
        >
          <p className="text-sm text-gray-700 dark:text-gray-300 px-3 py-3 space-y-4">
            Az oldalunk sütiket (cookie-kat) használ a működéshez, a felhasználói élmény
            javításához, valamint statisztikai és marketing célokra. Kérjük, válassza ki,
            mely sütik használatát engedélyezi.
          </p>

          <div className="px-3 py-3 space-y-4">
            <div className="flex flex-wrap gap-3">
              <Button onClick={acceptCookies} className="sm:w-auto flex-1" disabled={isSaving}>
                {isSaving ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Mentés...
                  </span>
                ) : (
                  "Összes elfogadása"
                )}
              </Button>

              <Button onClick={acceptCookies} className="sm:w-auto flex-1" disabled={isSaving}>
                {isSaving ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Mentés...
                  </span>
                ) : (
                  "Ajánlott beállítások"
                )}
              </Button>

              <Button onClick={() => setDetailedOpen(true)} disabled={isSaving}>
                Részletes beállítás
              </Button>

              <CookieSettingsPanel open={detailedOpen} onClose={() => setDetailedOpen(false)} />
            </div>
          </div>
        </BoxBlock>
      </div>
    </>
  );
}